import { useState } from "react";
import {
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from "firebase/auth";
import { auth } from "@/firebase/firebase-config";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, XCircle } from "lucide-react";

interface TwoFactorVerifyProps {
  resolver: MultiFactorResolver;
  recaptchaSolved?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export function TwoFactorVerify({ resolver, recaptchaSolved = false, onSuccess, onCancel }: TwoFactorVerifyProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);

  // Determine if this is SMS or TOTP MFA
  const selectedHint = resolver.hints[0];
  const isSMS = selectedHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID;

  // For SMS, we need to send the code first
  const sendSMSCode = async () => {
    setLoading(true);
    setError("");
    
    try {
      const phoneInfoOptions = {
        multiFactorHint: selectedHint,
        session: resolver.session,
      };
      
      const phoneAuthProvider = new PhoneAuthProvider(auth());
      const vId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier);
      setVerificationId(vId);
      if (import.meta.env.DEV) {
        console.log("SMS code sent!");
      }
    } catch (err: any) {
      if (import.meta.env.DEV) {
        console.error("Error sending SMS code:", err);
      }
      setError("Failed to send SMS code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let multiFactorAssertion;

      if (isSMS) {
        // SMS MFA verification
        if (!verificationId) {
          setError("Verification ID not found. Please try again.");
          return;
        }
        const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
        multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
      } else {
        // TOTP MFA verification
        multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
          selectedHint.uid,
          verificationCode
        );
      }

      // Complete sign-in with the assertion
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
      
      // Force token refresh to get updated claims with MFA verification
      if (userCredential.user) {
        await userCredential.user.getIdToken(true);
      }
      
      onSuccess();
    } catch (err: any) {
      if (import.meta.env.DEV) {
        console.error("Error verifying 2FA code:", err);
      }
      setError("Invalid code. Please try again.");
      setVerificationCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Two-Factor Authentication Required
        </CardTitle>
        <CardDescription>
          {isSMS 
            ? "Enter the code sent to your phone" 
            : "Enter the 6-digit code from your authenticator app"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSMS && !verificationId && (
          <div className="space-y-2">
            {!recaptchaSolved && (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Please complete the reCAPTCHA verification above first
              </p>
            )}
            <Button onClick={sendSMSCode} disabled={loading || !recaptchaSolved} className="w-full">
              {loading ? "Sending..." : "Send SMS Code"}
            </Button>
          </div>
        )}

        {(!isSMS || verificationId) && (
          <>
            <div className="space-y-2">
              <Label htmlFor="2fa-code">Verification Code</Label>
              <Input
                id="2fa-code"
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter" && verificationCode.length === 6) {
                    handleVerify();
                  }
                }}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1"
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <Button variant="outline" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
            </div>
          </>
        )}

        <p className="text-xs text-muted-foreground text-center">
          {isSMS 
            ? "Didn't receive the code? Check your phone and try resending."
            : "Lost access to your authenticator app? Contact your system administrator."}
        </p>
      </CardContent>
    </Card>
  );
}
